import { Update } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';
import {
    findIndex,
    keyBy,
    union,
    omit,
    difference,
    find,
    last,
    flatten,
    isEmpty,
    first,
    isUndefined,
    isString,
} from 'lodash-es';

import * as CpActions from './cp.actions';
import { cpInitialState, cpNodeAdapter, cpNodeCustomSelectors, CpState, INITIAL_DIALOG_CONFIG } from './cp.state';
import { isNotUndefined } from '../cp-configuration/utils/utils';
import { CpFlatNode } from '../cp-node';

export const reducer = createReducer(
    cpInitialState,
    /**
     * Initialize: imposta lo stato della pagina.
     */
    on(CpActions.cpInitialize, (state, { page }) => {
        return {
            ...state,
            page,
        };
    }),
    /**
     * AddNodes: aggiungi uno o più CpNode allo stato.
     */
    on(CpActions.cpAddNodes, (state, { nodes, parentId }) => {
        const parentNode = state.nodes.entities[parentId];
        if (!parentNode) {
            console.error(`CpReducer on cpAddNodes: could not find parent node ${parentId}`);

            return state;
        }

        const isFirstChild = parentNode.children.length === 0;
        const targetId = isFirstChild ? parentId : last(parentNode.children);
        const insertIndex = 1 + findIndex(state.nodes.ids, (id) => id === targetId);
        const nodeIds = nodes.map((n) => n.id);

        return {
            ...state,
            nodes: {
                ...state.nodes,
                entities: {
                    ...state.nodes.entities,
                    ...keyBy(nodes, 'id'),
                    [parentId]: {
                        ...parentNode,
                        children: union(parentNode.children, nodeIds),
                    },
                },
                ids: [...state.nodes.ids.slice(0, insertIndex), ...nodeIds, ...state.nodes.ids.slice(insertIndex)],
            },
        };
    }),
    on(CpActions.cpAddNodesToRoot, (state, { nodes }) => ({
        ...state,
        nodes: cpNodeAdapter.addMany(nodes, state.nodes),
    })),
    /**
     * FilterNodes: modifica il filtro sul navigator.
     */
    on(CpActions.cpFilterNodes, (state, { filter }) => ({
        ...state,
        navigator: {
            ...state.navigator,
            filter,
        },
    })),
    on(CpActions.cpFilterTextChange, (state, { text }) => ({
        ...state,
        navigator: {
            ...state.navigator,
            filter: {
                ...state.navigator.filter,
                text,
            },
        },
    })),
    /**
     * Initialize: imposta lo stato iniziale.
     */
    on(CpActions.cpInitializeNodes, (state, { nodes, rootNode }) => {
        return {
            ...cpInitialState,
            nodes: {
                ...cpNodeAdapter.setAll(nodes, state.nodes),
                root: rootNode ?? null,
            },
            selectedNode: isEmpty(nodes) ? null : (first(nodes) as any).id,
        };
    }),
    on(CpActions.cpRemoveNodes, (state, { nodes }) => {
        const allIdsToRemove = nodes.concat(
            flatten(nodes.map((node) => cpNodeCustomSelectors.selectTreeChildren(state.nodes, node))),
        );
        const parentIds = nodes.map((id) => state.nodes.entities[id]?.parentId).filter(isString);
        const parentNodes = parentIds.map((id) => state.nodes.entities[id]).filter(isNotUndefined);

        const selectedNode = !isUndefined(find(allIdsToRemove, (id) => id === state.selectedNode))
            ? null
            : state.selectedNode;

        return {
            ...state,
            nodes: {
                ...state.nodes,
                entities: {
                    ...omit(state.nodes.entities, allIdsToRemove),
                    ...keyBy(
                        parentNodes.map((parent) => ({
                            ...parent,
                            children: difference(parent.children, allIdsToRemove),
                        })),
                        'id',
                    ),
                },
                ids: difference(state.nodes.ids, allIdsToRemove),
            },
            selectedNode,
        };
    }),
    /**
     * SafeExit: Resetta allo stato iniziale.
     */
    on(CpActions.cpSafeExit, (_) => cpInitialState),
    /**
     * SelectNode: Imposta un dato nodo come selezionato.
     */
    on(CpActions.cpSelectNode, (state, { id }) => {
        if (id === state.selectedNode) {
            return state;
        }

        return { ...state, selectedNode: id };
    }),
    on(CpActions.cpSetDialogConfig, (state, { config }) => ({
        ...state,
        dialog: {
            ...INITIAL_DIALOG_CONFIG,
            ...config,
        },
    })),
    on(CpActions.cpUpdateDialogConfig, (state, { config }) => ({
        ...state,
        dialog: {
            ...state.dialog,
            ...config,
        },
    })),
    on(CpActions.cpToggleFilterButton, (state, { status }) => ({
        ...state,
        navigator: {
            ...state.navigator,
            filter: {
                ...state.navigator.filter,
                status: {
                    ...state.navigator.filter.status,
                    [status]: !state.navigator.filter.status[status],
                },
            },
        },
    })),
    on(CpActions.cpUpdateAllNodes, (state, changes) => {
        const nodeIds = cpNodeAdapter.getSelectors().selectIds(state.nodes) as string[];
        const updates: Update<CpFlatNode>[] = nodeIds.map((id) => ({ id, changes }));

        return {
            ...state,
            nodes: cpNodeAdapter.updateMany(updates, state.nodes),
        };
    }),
    on(CpActions.cpUpdateNode, (state, update) => {
        return {
            ...state,
            nodes: cpNodeAdapter.updateOne(update, state.nodes),
        };
    }),
    on(CpActions.cpUpdateNodes, (state, { nodes }) => {
        return {
            ...state,
            nodes: cpNodeAdapter.updateMany(nodes, state.nodes),
        };
    }),
);

export function cpReducer(state: CpState | undefined, action: Action) {
    return reducer(state, action);
}
